import { reactive } from 'vue';
import { taxConfigV2Api } from '../services/taxConfigV2Api';

/**
 * Reactive cache for the Tax Configuration V2 pages. The live API
 * (`services/taxConfigV2Api.js`) is the source of truth; this store holds the
 * mapped, UI-shaped copy the V2 views/dialogs render.
 *
 * V2 is camelCase end-to-end — the V2 DTOs are already camelCase, so (unlike the
 * V1 store) there is NO snake_case bridge. The V2 dialogs are fresh components
 * that speak the DTO shape directly.
 *
 * SHAPE: the V2 landing page is a TREE (year → config version → question set),
 * so config versions are cached PER YEAR (`versionsByYear[yearID]`) and fetched
 * lazily when a year row is expanded — there is no "currently selected year"
 * anymore. `versionsByYear[yearID] === undefined` means "never loaded" (a
 * re-expand retries); a failed re-fetch keeps the last known list in place and
 * only records `versionsErrorByYear[yearID]`.
 *
 * @typedef {Object} TaxYearV2
 * @property {number} yearID @property {string} taxYearCode @property {number} beYear @property {number} year
 * @property {?string} description @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 * @property {?string} updatedDate @property {boolean} isActive
 *
 * @typedef {Object} TaxConfigVersionV2
 * @property {number} versionID @property {number} yearID @property {string} versionCode
 * @property {?string} description @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 * @property {?string} updatedDate @property {boolean} isActive
 *
 * @typedef {Object} TaxCategoryV2
 * @property {number} categoryID @property {number} versionID @property {string} categoryCode
 * @property {?string} label @property {number} sortOrder @property {boolean} isActive  // one display name, no Th/En split
 *
 * @typedef {Object} TaxConfigRuleV2
 * @property {number} ruleID @property {number} versionID @property {?string} categoryCode @property {string} fieldKey
 * @property {?string} label @property {'text'|'number'|'boolean'} valueType  // data type of the value
 * @property {number} sortOrder @property {boolean} isActive
 * (A rule row is a FIELD declaration — no rate/cap/amount/range/shared-cap/expense-option members.)
 *
 * @typedef {Object} TaxConfigV2Full
 *   FLAT payload from GET versions/{id}/full:
 *   { version: TaxConfigVersionV2, categories: TaxCategoryV2[], rules: TaxConfigRuleV2[], warnings: string[] }
 *   Rules are a SIBLING list — NOT nested inside each category; they soft-link by `categoryCode` (no FK).
 */

const state = reactive({
  /** @type {TaxYearV2[]} */
  years: [],
  /** @type {Object.<number, TaxConfigVersionV2[]>} config versions keyed by yearID (lazy, per expanded year) */
  versionsByYear: {},
  /** @type {Object.<number, boolean>} in-flight version fetch keyed by yearID */
  versionsLoadingByYear: {},
  /** @type {Object.<number, string>} last version-fetch error keyed by yearID ('' = none) */
  versionsErrorByYear: {},
  loading: false,
  error: null,
});

/**
 * Monotonic request counter per yearID (plain object — not reactive, it is bookkeeping
 * only). Guards against out-of-order responses of concurrent forced reloads.
 * @type {Object.<number, number>}
 */
const versionsSeqByYear = {};

function setError(err) {
  state.error = (err && (err.message || String(err))) || 'เกิดข้อผิดพลาด';
}

function clearError() {
  state.error = null;
}

// ── Years ────────────────────────────────────────────────────────────────
async function loadYears(includeInactive = true) {
  state.loading = true;
  clearError();
  try {
    const rows = await taxConfigV2Api.getYears(includeInactive);
    state.years = Array.isArray(rows) ? rows : [];
    return state.years;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.loading = false;
  }
}

/** @returns {?TaxYearV2} */
function findYear(yearID) {
  return state.years.find((y) => y.yearID === yearID) || null;
}

/** Optimistically merge/insert a year DTO into the cache. */
function upsertYear(dto) {
  if (!dto || dto.yearID == null) return;
  const idx = state.years.findIndex((y) => y.yearID === dto.yearID);
  if (idx >= 0) state.years.splice(idx, 1, { ...state.years[idx], ...dto });
  else state.years.push({ ...dto });
}

function removeYear(yearID) {
  const idx = state.years.findIndex((y) => y.yearID === yearID);
  if (idx >= 0) state.years.splice(idx, 1);
  clearVersionsForYear(yearID);
}

/** Reflect a year's active flag locally (years are multi-active — no sibling flip). */
function reflectYearActive(yearID, isActive) {
  const y = findYear(yearID);
  if (y) y.isActive = Boolean(isActive);
}

// ── Config versions (cached per year) ────────────────────────────────────
/** @returns {TaxConfigVersionV2[]} the cached list of a year ([] when never loaded) */
function versionsForYear(yearID) {
  return state.versionsByYear[yearID] || [];
}

/** true once a year's versions have been fetched successfully at least once. */
function hasVersionsForYear(yearID) {
  return state.versionsByYear[yearID] !== undefined;
}

function isVersionsLoading(yearID) {
  return Boolean(state.versionsLoadingByYear[yearID]);
}

function versionsError(yearID) {
  return state.versionsErrorByYear[yearID] || '';
}

/**
 * Fetch (or re-fetch) ONE year's config versions into `versionsByYear[yearID]`.
 * A failed re-fetch never wipes a good cached list — it only records the error
 * (and re-throws so the caller can decide about snackbars); a failed FIRST fetch
 * leaves the bucket undefined so collapsing + re-expanding retries.
 * @returns {Promise<TaxConfigVersionV2[]>}
 */
async function loadVersionsForYear(yearID, includeInactive = true) {
  if (yearID == null) return [];
  // Per-year request sequence: two forced reloads (e.g. two quick active toggles)
  // can be in flight at once — only the LAST one issued may write the cache, so a
  // slow earlier response can never resurrect a stale active flag.
  const seq = (versionsSeqByYear[yearID] || 0) + 1;
  versionsSeqByYear[yearID] = seq;
  state.versionsLoadingByYear[yearID] = true;
  state.versionsErrorByYear[yearID] = '';
  clearError();
  try {
    const rows = await taxConfigV2Api.getVersions(yearID, includeInactive);
    if (seq !== versionsSeqByYear[yearID]) return versionsForYear(yearID);
    state.versionsByYear[yearID] = Array.isArray(rows) ? rows : [];
    return state.versionsByYear[yearID];
  } catch (err) {
    if (seq === versionsSeqByYear[yearID]) {
      state.versionsErrorByYear[yearID] = (err && err.message) || 'โหลดเวอร์ชันไม่สำเร็จ';
      setError(err);
    }
    throw err;
  } finally {
    // Only the latest request owns the spinner (a superseded one must not clear it).
    if (seq === versionsSeqByYear[yearID]) state.versionsLoadingByYear[yearID] = false;
  }
}

/** Drop one year's version cache (used after the year itself is deleted). */
function clearVersionsForYear(yearID) {
  delete state.versionsByYear[yearID];
  delete state.versionsLoadingByYear[yearID];
  delete state.versionsErrorByYear[yearID];
}

/** @returns {?TaxConfigVersionV2} search every cached year bucket by versionID */
function findVersion(versionID) {
  const keys = Object.keys(state.versionsByYear);
  for (let i = 0; i < keys.length; i += 1) {
    const hit = (state.versionsByYear[keys[i]] || []).find((v) => v.versionID === versionID);
    if (hit) return hit;
  }
  return null;
}

/** @returns {?number} the yearID owning a cached config version */
function yearIdOfVersion(versionID) {
  const keys = Object.keys(state.versionsByYear);
  for (let i = 0; i < keys.length; i += 1) {
    const bucket = state.versionsByYear[keys[i]] || [];
    if (bucket.some((v) => v.versionID === versionID)) return Number(keys[i]);
  }
  return null;
}

/**
 * Merge/insert a version DTO into its year's bucket. A DTO whose year has never
 * been loaded is ignored (the lazy fetch will bring it in on expand).
 */
function upsertVersion(dto) {
  if (!dto || dto.versionID == null) return;
  const yearID = dto.yearID != null ? dto.yearID : yearIdOfVersion(dto.versionID);
  if (yearID == null || state.versionsByYear[yearID] === undefined) return;
  const bucket = state.versionsByYear[yearID];
  const idx = bucket.findIndex((v) => v.versionID === dto.versionID);
  if (idx >= 0) bucket.splice(idx, 1, { ...bucket[idx], ...dto });
  else bucket.push({ ...dto });
}

function removeVersion(versionID) {
  const yearID = yearIdOfVersion(versionID);
  if (yearID == null) return;
  const bucket = state.versionsByYear[yearID];
  const idx = bucket.findIndex((v) => v.versionID === versionID);
  if (idx >= 0) bucket.splice(idx, 1);
}

/**
 * Single-active-per-year optimistic reflection: after activating a version,
 * flip every OTHER version of the SAME year to inactive locally so the badge is
 * truthful before the confirming re-fetch lands. Deactivating just clears the one.
 */
function reflectVersionActive(versionID, isActive) {
  const yearID = yearIdOfVersion(versionID);
  if (yearID == null) return;
  const bucket = state.versionsByYear[yearID];
  if (isActive) {
    bucket.forEach((v) => { v.isActive = v.versionID === versionID; });
  } else {
    const target = bucket.find((v) => v.versionID === versionID);
    if (target) target.isActive = false;
  }
}

// NOTE: there is deliberately NO full-config (editor) slice here — the V2 editor
// page (`views/TaxConfigurationV2Edit.vue`) talks to `taxConfigV2Api` directly.
// Add one here only when that page is actually migrated onto the store.

export const taxConfigV2Store = {
  state,
  clearError,
  setError,
  // years
  loadYears,
  findYear,
  upsertYear,
  removeYear,
  reflectYearActive,
  // versions (per year)
  loadVersionsForYear,
  versionsForYear,
  hasVersionsForYear,
  isVersionsLoading,
  versionsError,
  clearVersionsForYear,
  findVersion,
  yearIdOfVersion,
  upsertVersion,
  removeVersion,
  reflectVersionActive,
};

export default taxConfigV2Store;
